import { createSlider, getObserver, minWidthQuery, SkyLazyMedia } from '@theme/theme-global';

class SlideShow extends HTMLDivElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.sectionIndex = parseInt(this.section?.querySelector('[data-index]')?.dataset?.index ?? '1', 10);
    this.leftSlider = this.querySelector('[is="slide-show-left"]');
    this.rightSlider = this.querySelector('[is="slide-show-right"]');
    this.mobileSlider = this.querySelector('[is="slide-show-mobile"]');
    this._desktopMq = minWidthQuery(768);
    this._onBreakpoint = this._onBreakpoint.bind(this);
  }

  connectedCallback() {
    this.init();
    this._desktopMq.addEventListener('change', this._onBreakpoint);
  }

  disconnectedCallback() {
    this._desktopMq.removeEventListener('change', this._onBreakpoint);
  }

  _onBreakpoint() {
    this.createSliders();
  }

  init() {
    const aboveTheFoldSection = this.section && this.sectionIndex <= 2;
    const doInit = async () => {
      if (this._slidersInitializing) return;
      this._slidersInitializing = true;
      try {
        await this.createSliders();
        if (this.rightSlider?.sliderInstance) {
          this.setupSynchronizedControls();
        }
      } finally {
        this._slidersInitializing = false;
      }
    };

    if (aboveTheFoldSection) {
      doInit();
    } else if ('IntersectionObserver' in window) {
      getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false).observe(this, doInit);
    } else {
      doInit();
    }
  }

  createSliders() {
    const initSlider = async (sliderEl) => {
      if (!sliderEl?.dataset.params || sliderEl.sliderInstance) return;

      const params = this.parseParams(sliderEl);
      const swiper = await createSlider(sliderEl, params);
      sliderEl.sliderInstance = swiper;

      if (swiper) {
        this.attachSkyLazyMedia(swiper);
        this.setupVideoAutoplayPause(swiper, sliderEl);
        swiper.on('slideChangeTransitionEnd', () => {
          this.onSlideChangeTransitionEnd(swiper, sliderEl);
        });
        swiper.on('touchEnd', () => {
          setTimeout(() => {
            if (swiper.params.autoplay?.enabled && !swiper.autoplay.running) {
              swiper.autoplay.start();
            }
            this.setupVideoAutoplayPause(swiper, sliderEl);
          }, 150);
        });
      }
    };

    const desktop = this._desktopMq.matches;
    const targets = desktop
      ? [this.leftSlider, this.rightSlider]
      : [this.mobileSlider];

    return Promise.all(targets.map(initSlider)).then(() => {
      this.sliderInstance = desktop
        ? this.leftSlider?.sliderInstance
        : this.mobileSlider?.sliderInstance;
    });
  }
  parseParams(sliderEl) {
    const params = JSON.parse(sliderEl.dataset.params);
    const paginationType = sliderEl.dataset.paginationType;
    if (paginationType === 'numbers' && params.pagination) {
      params.pagination.renderBullet = function (index, className) {
        return `<span class="${className}">${index + 1}</span>`;
      };
    }
    return params;
  }

  setupSynchronizedControls() {
    if (!this.leftSlider?.sliderInstance || !this.rightSlider?.sliderInstance) return;

    const left = this.leftSlider.sliderInstance;
    const right = this.rightSlider.sliderInstance;

    left.on('slideChange', () => {
      setTimeout(() => {
        if (right.slides?.length) {
          const idx = Math.min(left.activeIndex, right.slides.length - 1);
          right.slideTo(idx);
        }
      }, 300);
    });

    right.on('slideChange', () => {
      setTimeout(() => {
        if (left.slides?.length) {
          const idx = Math.min(right.activeIndex, left.slides.length - 1);
          left.slideTo(idx);
        }
      }, 300);
    });
  }

  attachSkyLazyMedia(swiperInstance) {
    const activeSlide = swiperInstance.el?.querySelector('.swiper-slide-active');
    const activeMedia = activeSlide?.querySelectorAll('sky-lazy-media');
    activeMedia?.forEach((mediaEl) => {
      mediaEl._playTrigger = 'autoplay';
    });
  }

  onSlideChangeTransitionEnd(swiperInstance, sliderEl) {
    if (!swiperInstance?.el) return;
    const activeSlide = swiperInstance.el.querySelector('.swiper-slide-active');
    const activeMedia = activeSlide?.querySelectorAll('sky-lazy-media');
    activeMedia?.forEach((mediaEl) => {
      mediaEl._playTrigger = 'autoplay';
    });
    this.setupVideoAutoplayPause(swiperInstance, sliderEl);
  }

  setupVideoAutoplayPause(swiperInstance, sliderEl) {
    if (!swiperInstance || !sliderEl) return;

    const isEnabled = this.dataset.pauseForVideo === 'true';
    if (!isEnabled || !swiperInstance.params.autoplay?.enabled) return;

    const activeSlide = swiperInstance.el.querySelector('.swiper-slide-active');
    if (!activeSlide) return;

    const initVideoLogic = (video) => {
      if (video.loop) return;

      // Reset video to start
      video.currentTime = 0;
      video
        .play()
        .then(() => {
          swiperInstance.autoplay.stop();
        })
        .catch((e) => console.warn('Autoplay blocked:', e));

      // Video end trigger
      video.onended = () => {
        video.onended = null;
        swiperInstance.autoplay.start();
        swiperInstance.slideNext();
      };
    };

    const deferred = activeSlide.querySelector('sky-lazy-media');
    if (deferred) {
      const video = deferred.querySelector('video');
      if (video) {
        initVideoLogic(video);
      } else {
        // Listen for video injection from your global SkyLazyMedia class
        deferred.addEventListener(
          'loaded',
          () => {
            const newVideo = deferred.querySelector('video');
            if (newVideo) initVideoLogic(newVideo);
          },
          { once: true },
        );
      }
    }
  }
}

// Optional hook: split slideshow – manual play → pause all others; autoplay → don't pause (both sides can play)
if (typeof SkyLazyMedia !== 'undefined') {
  SkyLazyMedia.beforePauseOthers = (playingEl, others) => {
    const inSplitSlideshow = playingEl.closest('[data-slideshow-split]');
    if (!inSplitSlideshow) return undefined;
    if (playingEl._playTrigger === 'manual') return others;
    return [];
  };
}

if (!customElements.get('slide-show')) {
  customElements.define('slide-show', SlideShow, { extends: 'div' });
}
